import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowUpRight, Linkedin, Facebook, Twitter, Instagram, Heart, Share2 } from 'lucide-react';
import { storyPosts, stories, CONTACT } from '../../mock';
import PageHeader from '../innovher/PageHeader';
import Magnetic from '../innovher/Magnetic';
import SEO from '../SEO';

export default function StoryPostPage() {
  const { slug } = useParams();
  const post = storyPosts[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug]);

  if (!post) return <Navigate to="/issi" replace />;

  const related = stories.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO 
        title={`${post.title} | InnovHer Stories`}
        description={post.summary}
        keywords={`${post.tag}, InnovHer stories, Rajasthan founders, venture builder`}
      />
      <PageHeader
        eyebrow={post.tag}
        title={post.title}
        subtitle={post.summary}
        image={post.image}
        breadcrumbs={[{ label: 'iSSi', to: '/issi' }, { label: post.title.length > 40 ? post.title.slice(0, 40) + '\u2026' : post.title }]}
      />

      <section className="relative bg-[#08080a] py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8 reveal-up">
            <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[16/9]">
              <img src={post.image} alt="" className="w-full h-full object-cover" />
            </div>

            <div className="mt-10 space-y-5 text-white/75 leading-relaxed text-lg">
              {post.body.map((p, i) => (
                <p key={i} className={i === 0 ? 'text-white/85 text-xl' : ''}>{p}</p>
              ))}
            </div>

            <hr className="my-10 border-white/10" />
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-white/50 text-sm">Tags:</span>
              {post.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-xs text-white/80">#{t}</span>
              ))}
            </div>
          </article>

          <aside className="lg:col-span-4 reveal-up">
            <div className="sticky top-32 space-y-6">
              <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase">5 min read</div>
                <div className="mt-4 text-white/80">Share this post:</div>
                <div className="mt-3 flex gap-2">
                  {[
                    { Icon: Linkedin, href: CONTACT.socials.linkedin },
                    { Icon: Facebook, href: CONTACT.socials.facebook },
                    { Icon: Twitter, href: CONTACT.socials.twitter },
                    { Icon: Instagram, href: CONTACT.socials.instagram },
                  ].map(({ Icon, href }, i) => (
                    <a key={i} href={href} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-white/15 grid place-items-center text-white hover:bg-[#d42b92] hover:border-[#d42b92] transition">
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
                <button className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/85 hover:border-[#d42b92] hover:text-[#ff8ec5] transition">
                  <Heart className="w-4 h-4" /> Like it <span className="ml-1 inline-grid place-items-center min-w-6 h-6 px-1.5 rounded-full bg-[#d42b92] text-white text-xs">8</span>
                </button>
              </div>

              <Magnetic>
                <Link to="/apply" data-cursor="hover" className="group inline-flex items-center gap-2 pl-6 pr-2 py-2 rounded-full bg-[#d42b92] text-white font-medium hover:bg-white hover:text-black transition">
                  Apply to Innovher
                  <span className="w-8 h-8 rounded-full bg-white text-[#d42b92] group-hover:bg-[#d42b92] group-hover:text-white grid place-items-center group-hover:rotate-45 transition">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </Link>
              </Magnetic>
            </div>
          </aside>
        </div>
      </section>

      <section className="relative bg-[#0a0a0c] py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">Related stories</h3>
            <Link to="/issi" className="text-sm text-white/60 hover:text-[#ff8ec5] inline-flex items-center gap-1">All stories <Share2 className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {related.map((s) => (
              <Link key={s.slug} to={s.to} className="group relative rounded-3xl overflow-hidden border border-white/10 hover:border-[#d42b92] transition" data-cursor="hover">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-medium text-white tracking-wide border border-white/20">{s.tag}</div>
                  <div className="absolute bottom-4 left-5 right-5 text-white">
                    <div className="text-base font-semibold leading-tight">{s.title}</div>
                    <div className="text-white/70 text-xs mt-1">{s.date}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
